import { useState, useEffect } from 'react';
import { Tabs, Descriptions, Table, Button, Modal, Input, InputNumber, message, Popconfirm, Tag, Space, Card, Statistic, Row, Col, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, UserOutlined, PercentageOutlined, BarChartOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageModels, setUsageModels] = useState([]);
  const [flowRecords, setFlowRecords] = useState([]);
  const [flowTotal, setFlowTotal] = useState(0);
  const [flowPage, setFlowPage] = useState(1);
  const [flowLoading, setFlowLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ model_pattern: '', discount_rate: 0.90 });
  const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const now = new Date();
  const [dateRange, setDateRange] = useState({ start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: toDateStr(now) });

  useEffect(() => {
    fetchDiscounts();
  }, [id]);

  useEffect(() => {
    fetchUsage();
  }, [id, dateRange]);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getUserDiscounts(id);
      if (res.code === 0 && res.data?.discounts) setDiscounts(res.data.discounts);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchUsage = async () => {
    setUsageLoading(true);
    try {
      const res = await adminAPI.getUserUsage(id, dateRange);
      if (res.code === 0) {
        setUsageData({...res.data?.summary, balance: res.data?.balance});
        setUsageModels(res.data?.models || []);
      }
    } catch (e) { console.error(e); }
    setUsageLoading(false);
  };

  const fetchFlowRecords = async (pg = 1) => {
    setFlowLoading(true);
    try {
      const res = await adminAPI.getUserUsageRecords(id, { start: dateRange.start, end: dateRange.end, page: pg, page_size: 20 });
      if (res.code === 0) {
        setFlowRecords(res.data?.records || []);
        setFlowTotal(res.data?.total || 0);
        setFlowPage(pg);
      }
    } catch (e) { console.error(e); }
    setFlowLoading(false);
  };

  const handleSave = async () => {
    if (!form.model_pattern.trim()) { message.warning('请输入模型匹配模式'); return; }
    try {
      let res;
      if (editingId) {
        res = await adminAPI.updateDiscount(editingId, { discount_rate: form.discount_rate, model_pattern: form.model_pattern });
      } else {
        res = await adminAPI.createUserDiscount(id, form);
      }
      if (res.code === 0) { message.success(editingId ? '已更新' : '已创建'); setModalOpen(false); setEditingId(null); fetchDiscounts(); }
      else { message.error(res.message || '操作失败'); }
    } catch (e) { message.error('操作失败'); }
  };

  const handleDelete = async (discountId) => {
    try { await adminAPI.deleteDiscount(discountId); message.success('已删除'); fetchDiscounts(); }
    catch (e) { message.error('删除失败'); }
  };

  const discountColumns = [
    { title: '模型匹配', dataIndex: 'model_pattern', render: v => <Tag color="blue">{v}</Tag> },
    { title: '折扣', dataIndex: 'discount_rate', render: v => (v>=1||!v) ? <Tag>无折扣</Tag> : <Tag color={v<0.8?'red':v<0.95?'orange':'green'}>{parseFloat((v*10).toFixed(1))}折</Tag> },
    { title: '状态', dataIndex: 'status', render: v => <Tag color={v==='active'?'green':'default'}>{v}</Tag> },
    { title: '操作', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EditOutlined/>} onClick={() => { setEditingId(r.id); setForm({model_pattern:r.model_pattern,discount_rate:r.discount_rate}); setModalOpen(true); }}>编辑</Button>
        <Popconfirm title="确定删除?" onConfirm={() => handleDelete(r.id)}><Button size="small" danger icon={<DeleteOutlined/>}>删除</Button></Popconfirm>
      </Space>
    )},
  ];

  const modelColumns = [
    { title: '模型', dataIndex: 'model', key: 'model' },
    { title: '调用次数', dataIndex: 'call_count', key: 'calls' },
    { title: 'Input Tokens', dataIndex: 'in_tokens', key: 'in' },
    { title: 'Output Tokens', dataIndex: 'out_tokens', key: 'out' },
    { title: '金额(USD)', dataIndex: 'amount', key: 'amt', render: v => `$${(v||0).toFixed(4)}` },
    { title: '折扣', dataIndex: 'avg_discount', key: 'disc', render: v => (v>=1||!v) ? <Tag>无折扣</Tag> : <Tag>{parseFloat((v*10).toFixed(1))}折</Tag> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Button icon={<ArrowLeftOutlined/>} onClick={() => navigate('/users')} style={{ marginBottom: 16 }}>返回用户列表</Button>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions title={<span><UserOutlined/> 用户信息</span>} column={2} size="small">
          <Descriptions.Item label="用户ID">{id}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs defaultActiveKey="discounts">
        <Tabs.TabPane tab={<span><PercentageOutlined/> 折扣管理</span>} key="discounts">
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message="折扣规则说明" description={
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <p><b>匹配优先级</b>（从高到低）：<br/>
              ① 精确匹配（model_pattern = 完整模型名如 <code>seedance-2.0-idle</code>）<br/>
              ② 前缀通配（model_pattern = <code>deepseek-*</code> 匹配 deepseek-chat / deepseek-v4-pro 等）<br/>
              ③ 全局通配（model_pattern = <code>*</code> 匹配所有模型）<br/>
              ④ 未命中 → API Key 全局折扣（<code>api_keys.discount_rate</code>）→ 无折扣(1.00)</p>
              <p><b>填写规则</b>：model_pattern 填的是<b>模型名称</b>（model_name），不是显示名称。支持 <code>*</code> 通配符。例如：<br/>
              <code>deepseek-chat</code>、<code>seedance-2.0-idle</code>、<code>deepseek-*</code>、<code>*</code></p>
              <p><b>折扣率</b>：0.70=7折（用户付70%），1.00=原价/无折扣。新规则不影响历史账单。</p>
            </div>
          } />
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined/>} onClick={() => { setEditingId(null); setForm({model_pattern:'',discount_rate:0.90}); setModalOpen(true); }}>
              添加折扣规则
            </Button>
          </div>
          <Table columns={discountColumns} dataSource={discounts} rowKey="id" loading={loading} size="small" pagination={false} />
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><BarChartOutlined/> 用量对账</span>} key="usage">
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} placeholder="开始日期" />
              <span>~</span>
              <Input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} placeholder="结束日期" />
              <Button type="primary" onClick={fetchUsage}>查询</Button>
              <Button onClick={() => { const d = new Date(); setDateRange({start: toDateStr(new Date(d.getFullYear(), d.getMonth(), 1)), end: toDateStr(d)}); }}>本月</Button>
            </Space>
          </div>
          {usageData && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}><Card><Statistic title="总消耗" value={usageData?.total_cost || 0} precision={4} prefix="$" /></Card></Col>
              <Col span={6}><Card><Statistic title="调用次数" value={usageData?.call_count || 0} /></Card></Col>
              <Col span={6}><Card><Statistic title="余额" value={usageData?.balance || 0} precision={4} prefix="$" /></Card></Col>
            </Row>
          )}
          <Table columns={modelColumns} dataSource={usageModels} rowKey="model" loading={usageLoading} size="small"
            summary={() => usageModels.length > 0 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell><strong>合计</strong></Table.Summary.Cell>
                <Table.Summary.Cell>{usageModels.reduce((s,r) => s+(r.call_count||0), 0)}</Table.Summary.Cell>
                <Table.Summary.Cell>{usageModels.reduce((s,r) => s+(r.in_tokens||0), 0)}</Table.Summary.Cell>
                <Table.Summary.Cell>{usageModels.reduce((s,r) => s+(r.out_tokens||0), 0)}</Table.Summary.Cell>
                <Table.Summary.Cell><strong>${usageModels.reduce((s,r) => s+(r.amount||0), 0).toFixed(4)}</strong></Table.Summary.Cell>
                <Table.Summary.Cell></Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null}
            pagination={false} />
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><ThunderboltOutlined/> 用量流水</span>} key="flow">
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Input type="date" value={dateRange.start} onChange={e => setDateRange({...dateRange, start: e.target.value})} placeholder="开始日期" />
              <span>~</span>
              <Input type="date" value={dateRange.end} onChange={e => setDateRange({...dateRange, end: e.target.value})} placeholder="结束日期" />
              <Button type="primary" onClick={() => fetchFlowRecords(1)}>查询</Button>
              <Button onClick={() => { const d = new Date(); setDateRange({start: toDateStr(new Date(d.getFullYear(), d.getMonth(), 1)), end: toDateStr(d)}); }}>本月</Button>
            </Space>
          </div>
          <Table
            columns={[
              { title: '时间', dataIndex: 'created_at', width: 160, render: v => v ? new Date(v).toLocaleString() : '-' },
              { title: '模型', dataIndex: 'model' },
              { title: 'Input', dataIndex: 'input_tokens', render: v => v ? Number(v).toLocaleString() : '-' },
              { title: 'Output', dataIndex: 'output_tokens', render: v => v ? Number(v).toLocaleString() : '-' },
              { title: '金额(USD)', dataIndex: 'amount', render: v => `$${(v||0).toFixed(6)}` },
              { title: '折扣', dataIndex: 'discount_rate', render: v => (v>=1||!v) ? '无折扣' : `${parseFloat((v*10).toFixed(1))}折` },
            ]}
            dataSource={flowRecords}
            rowKey={(r,i) => `${r.created_at}-${i}`}
            loading={flowLoading}
            size="small"
            pagination={{ current: flowPage, total: flowTotal, pageSize: 20, onChange: (p) => fetchFlowRecords(p) }}
          />
        </Tabs.TabPane>
      </Tabs>

      <Modal title={editingId ? '编辑折扣' : '添加折扣'} open={modalOpen} onOk={handleSave} onCancel={() => setModalOpen(false)} destroyOnClose>
        <div style={{ marginBottom: 16 }}>
          <label>模型匹配模式</label>
          <Input placeholder="填模型名称(非显示名)如 deepseek-chat 或 deepseek-*" value={form.model_pattern} onChange={e => setForm({...form, model_pattern: e.target.value})} />
          <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>填<b>模型名称(model_name)</b>，不是显示名称。* 匹配全部，deepseek-* 匹配 deepseek 系列</div>
        </div>
        <div>
          <label>折扣率 ({(form.discount_rate*100).toFixed(0)}折 = {((1-form.discount_rate)*100).toFixed(0)}% off)</label>
          <InputNumber min={0.01} max={1.00} step={0.01} value={form.discount_rate} onChange={v => setForm({...form, discount_rate: v})} style={{ width: '100%' }} />
        </div>
      </Modal>
    </div>
  );
};

export default UserDetail;
